import { createContext, useContext } from "react";

/**
 * `next-auth/react` replaced, exactly as `next-themes` was (see ./theme.tsx).
 *
 * The template's `layouts/profile-menu.tsx` imports `signOut` from `next-auth/react` and renders a
 * hard-coded "John Doe". Both are demo stand-ins for something the host app owns. `next-auth` is a
 * server-side session library and this is a client-only SPA talking to its own BFF, so there is
 * nothing to port — only a seam to leave where the copied file expects one.
 *
 * The host app supplies the real implementation at the root; the copied file's only edit is its
 * import line, which is what the de-Next rule permits (FR-004).
 */

export type SessionUser = {
  name: string;
  email?: string;
  image?: string;
  /** The account's own role, as the identity service reports it. */
  role?: string;
  /** The role the local role-matrix resolved to. Both are shown because GAP-5 lets them differ. */
  matrixRole?: string;
};

type SessionValue = {
  user: SessionUser | null;
  signOut: () => void | Promise<void>;
  /** Whether the profile menu offers "Audit trail" — the host's live `can()`, never a role check. */
  mayReadAudit?: boolean;
};

const SessionContext = createContext<SessionValue>({ user: null, signOut: () => {} });

export function SessionProvider({ value, children }: React.PropsWithChildren<{ value: SessionValue }>) {
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export const useSession = () => useContext(SessionContext);
